import React, { useRef, useState, useEffect, Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { webBtnSize, mobileBtnSize, widgetPadding } from './constants'
import { MapWidgetEvent } from './event'
const SearchBar = ({
    options,
    searchValue,
    setSearchValue,
    selectAsset,
    cleanSelectedAsset,
    setHighlightAsset,
    dashTheme,
    isMobile
}: {
    options: Array<MapWidgetEvent>
    searchValue: string
    setSearchValue: Dispatch<SetStateAction<string>>
    selectAsset: (assetName: string, animationType: 'panTo' | 'flyTo', e?: L.LeafletMouseEvent) => void
    setHighlightAsset: Dispatch<SetStateAction<string>>
    cleanSelectedAsset: (cleanSearch?: boolean) => void
    dashTheme: string
    isMobile: string
}): React.ReactElement => {
    const [result, setResult] = useState<Array<string>>([])
    const [showOptions, setShowOptions] = useState<boolean>(false)

    const inputRef = useRef(null)
    function onSelectAsset(assetName): void {
        selectAsset(assetName, 'flyTo')
        setSearchValue(assetName)
        setShowOptions(false)
        setHighlightAsset('')
        inputRef.current.blur()
    }

    function getResultList(): React.ReactElement {
        if (result.length && searchValue && showOptions) {
            return (
                <ul>
                    {result.map(el => {
                        return (
                            <li
                                key={el}
                                onMouseDown={(e: React.MouseEvent): void => e.preventDefault()}
                                onClick={(): void => onSelectAsset(el)}
                                onMouseEnter={(): void => {
                                    setHighlightAsset(el)
                                }}
                                onMouseOut={(): void => {
                                    setHighlightAsset('')
                                }}
                            >
                                <i className="fas fa-map-marker-check" /> {el}{' '}
                            </li>
                        )
                    })}
                </ul>
            )
        } else if (!result.length && searchValue && showOptions) {
            return (
                <div className="noResult">
                    <i className="fas fa-map-marker-alt-slash" /> <span> No results found </span>
                </div>
            )
        }
    }

    useEffect(() => {
        setResult(
            options
                .filter(option => option.asset.toLowerCase().includes(searchValue.toLowerCase()))
                .map(option => option.asset)
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue])

    function onChangeInput(e: React.ChangeEvent<HTMLInputElement>): void {
        const value = e.target.value

        setShowOptions(true)
        setSearchValue(value)
        setHighlightAsset('')
        cleanSelectedAsset()
    }

    function onClickOutside(): void {
        setTimeout(function() {
            setShowOptions(false)
            setHighlightAsset('')
        }, 100)
    }

    function onSubmitInput(event): void {
        event.preventDefault()
        setShowOptions(false)
        inputRef.current.blur()
    }
    const mobileClass = isMobile ? 'isMobile' : ''
    const activeClass = searchValue ? 'hasValue' : ''
    return (
        <SearchBarContainer dashTheme={dashTheme} className={`${mobileClass} ${activeClass}`}>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>): void => onSubmitInput(event)}>
                <input
                    ref={inputRef}
                    placeholder={'Search asset'}
                    onChange={onChangeInput}
                    value={searchValue}
                    onFocus={(): void => setShowOptions(true)}
                    onBlur={(): void => onClickOutside()}
                />
                <i className="fa fa-search" />
                {searchValue ? (
                    <span
                        id="searchCloseButton"
                        onClick={(e: React.MouseEvent): void => {
                            e.preventDefault()
                            cleanSelectedAsset(true)
                        }}
                    >
                        <i className="fa fa-times" />
                    </span>
                ) : null}
            </form>
            {getResultList()}
        </SearchBarContainer>
    )
}

const SearchBarContainer = styled.div`
    position: absolute;
    left: ${widgetPadding}px;
    top: 23px;
    z-index: 1001;
    width: ${webBtnSize}px;
    transition: all 0.3s;
    font-size: 13px;

    &.hasValue,
    &:hover,
    &:focus-within {
        width: 150px;
    }

    form {
        margin: 0;
        position: relative;
        width: 100%;

        i {
            position: absolute;
            left: 9px;
            top: 50%;
            transform: translateY(-50%);
            color: #37a3ef;
        }
        input {
            border: none;
            padding: 0;
            padding-left: ${webBtnSize}px;
            height: ${webBtnSize}px;
            box-shadow: 0 0px 3px rgba(0, 0, 0, 0.65);
        }
    }
    input,
    ul,
    .noResult {
        background-color: ${({ dashTheme }) => (dashTheme == 'light' ? 'white' : '#212a3e')};
        color: ${({ dashTheme }) => (dashTheme == 'light' ? '#364159' : '#d1d5d9')};
        border-radius: 3px;
        width: 100%;
    }

    ul,
    .noResult {
        margin-top: 2px;
        animation: 0.2s ease-in-out slideInFromTop forwards;
        max-height: 250px;
        overflow: auto;
        box-shadow: 0px 0px 2px rgba(68, 68, 68, 0.6);
        line-height: 26px;
        i {
            margin: 0 7px 0 9px;
        }
        li {
            line-height: inherit;
            cursor: pointer;
            &:hover {
                background-color: ${({ dashTheme }) => (dashTheme == 'light' ? 'rgba(68, 68, 68, 0.05)' : '#364159')};
            }
        }

        @keyframes slideInFromTop {
            0% {
                max-height: 0px;
                opacity: 0;
            }
            100% {
                max-height: 250px;
                opacity: 1;
            }
        }
    }

    #searchCloseButton {
        background: none;
        border: none;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        opacity: 0;
        animation: closingXAppear 0.3s 0.3s linear forwards;
        cursor: pointer;

        i {
            left: unset;
            right: 10px;
        }
        @keyframes closingXAppear {
            100% {
                opacity: 1;
            }
        }
    }

    &.isMobile {
        font-size: 16px;
        overflow: hidden;
        width: ${mobileBtnSize}px;

        &:hover {
            width: calc(100% - ${widgetPadding * 2}px);
            ul,
            .noResult {
                display: block;
            }
            #searchCloseButton {
                display: unset;
            }
        }

        form {
            i {
                transform: translate3d(0, -50%, 0);
                left: 13px;
            }
            input {
                transform: translate3d(0, 0, 0);
                height: ${mobileBtnSize}px;
                padding-left: ${mobileBtnSize}px;
            }
        }
        ul,
        .noResult {
            transform: translate3d(0, 0, 0);
            padding: 0 15px;
            line-height: ${mobileBtnSize}px;
            display: none;
        }
        #searchCloseButton {
            display: none;
            width: ${mobileBtnSize}px;
            height: ${mobileBtnSize}px;
            opacity: 1;
            transform: translate(-100%, -50%);
            i {
                right: 40%;
            }
        }
    }
`

export default SearchBar
